import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { getSessionToken } from "@/api/auth";
import type { Decision, Item, Meta, RoomSection } from "./content";

// RV-1 — Launch Reviews API (the Agents server, /api/agents/review-center). Every call carries
// the Launch OS session token; the server scopes everything to the caller's agency.

const BASE = `${DOCS_API_DOMAIN}/api/agents/review-center`;

// RV-7 — a page opened from a share link talks to /api/review-share/<code> instead: the code
// stands for the one review, so "/<id>/decide" becomes "/decide", and no sign-in is sent.
// RV-4 — the "My reviews" link (/my/<code>) works the same way for any review of its account:
// /api/review-inbox/<code> lists them, /api/review-inbox/<code>/r/<id> is one review.
let OPEN: { base: string; list?: string } | null = null;
export const useShareLink = (token: string | null) => { OPEN = token ? { base: `${DOCS_API_DOMAIN}/api/review-share/${encodeURIComponent(token)}` } : null; };
export const useInboxLink = (code: string | null, id?: string) => {
  const root = `${DOCS_API_DOMAIN}/api/review-inbox/${encodeURIComponent(code || "")}`;
  OPEN = code ? { base: id ? `${root}/r/${encodeURIComponent(id)}` : root, list: root } : null;
};
export const isShared = () => !!OPEN;
const route = (path: string) => {
  if (!OPEN) return BASE + path;
  if (path === "" && OPEN.list) return OPEN.list;
  const rest = path.replace(/^\/[^/]+/, ""); // drop the review id: the link already names the review
  return `${OPEN.base}${rest}`;
};

export type ReviewSummary = {
  id: string; title: string; client: string | null; type: "short" | "long" | "page" | "decision" | "questions" | "media";
  status: "open" | "done"; receivedAt: string; sender: string | null; items: number; decided: number;
  deletedAt: string | null; purgeOn?: string;
  // nested reviews: set on a document room while an open review links it; set on that review
  partOf?: { id: string; title: string } | null; docs?: string[]; docNotes?: number; docDecided?: number;
  // SH-4 — your level on it: "owner" (yours, or you own the company), else what it was shared with you as
  access?: { level: AccessLevel }; createdBy?: string | null;
};
export type AccessLevel = "owner" | "comment" | "view";
export type Teammate = { name: string; email: string; initials: string; color: string | null; role: string };
export type ShareList = { team: "comment" | "view" | null; people: { email: string; level: "comment" | "view" }[]; emailed?: string[]; notEmailed?: string[] };
export type ReviewItem = Item & {
  date?: string; kind?: string; grade?: number; rec?: string; brief?: string; briefAudio?: string; briefTimes?: string; readAudio?: string; readTimes?: string;
  facts?: string[]; changes?: { platform?: string; why?: string }[];
  // RV-6 — Decision room (a SIR) and Questions room (one question per card)
  sir?: Sir; sirType?: string; group?: string; details?: string;
  context?: string; // markdown: the background needed to decide, shown open above the question
  doc?: { id: string; label?: string; share?: string }; // a linked document (Long form room): "📄 Open the document" on the card
  media?: { id: string; name?: string }; // a media room: the one Launch Media video this card shows
  history?: { round: number; media: { id: string; name?: string }; pass?: boolean }[]; // earlier rounds' videos, oldest first
  cid?: string; story?: string; looksLike?: string; pass?: boolean; // a media room card: the condition it proves, its story, what to look for, and whether the test passed
  room?: import("./frames").RoomDraw; // a copy-room page: what's needed to redraw it with sections mixed
};
export type Sir = {
  situation?: string;
  information?: { accomplish?: string; impacted?: string; past_attempts?: string; root_cause?: string; facts?: string[]; options?: { columns?: string[]; rows?: string[][]; summary?: string }; risks?: { risk: string; handling?: string }[]; required_by?: string; scope?: { in?: string[] | string; out?: string[] | string } };
  recommendation?: { pick?: string; cost?: string; how_it_solves?: string; why_not_others?: string[]; implementation?: string; done_means?: string; progress?: string; dates?: string[] };
  asks?: { what: string; who?: string; by?: string }[]; needs_from_client?: { what: string; who?: string; by?: string }[];
  assumptions?: string[]; next_step?: string; can_i_proceed?: string; appendices?: { letter?: string; title: string; body: string }[];
};
export type Note = { id: string; itemId: string; text: string; quote: string | null; version: string | null; by: string | null; at: string; file: string | null; transcript: string | null; anchor?: string; editedAt?: string; kind?: "frame"; frame?: string; round?: number };
export type Review = {
  id: string; title: string; client: string | null; type: ReviewSummary["type"]; status: "open" | "done";
  receivedAt: string; source: { channel?: string; sender?: string; link?: string }; meta: Meta & { avatar?: string };
  sections?: RoomSection[]; // deploy 9: cards grouped into sections, each with an intro card and a map
  items: ReviewItem[]; decisions: Record<string, Decision & { by?: string; at?: string }>; notes: Note[]; deletedAt: string | null;
  // SH-1..SH-3 — sent by a server with sharing: who you are, who's on the team, and what you may do
  me?: string | null; people?: Record<string, { name: string; initials: string; color: string | null }>;
  access?: { level: AccessLevel; canDecide: boolean; canShare: boolean };
};

async function call<T>(path: string, init: { method?: "GET" | "POST"; body?: unknown } = {}): Promise<T> {
  const token = OPEN ? null : getSessionToken();
  const res = await fetch(route(path), {
    method: init.method || "GET",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: init.body ? JSON.stringify(init.body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || data.error) throw new Error((data && data.error) || "Couldn't reach Reviews. Try again.");
  return data as T;
}

// SH-5 — the share window: your team, and who this review is shared with (owner only)
export const getTeam = () => call<{ team: Teammate[] }>("/team").then((d) => d.team);
// SH-7 — each person's Team manager role and Share permission, and what the viewer may do
export type TeamInfo = { team: (Teammate & { manager: boolean; share: boolean; owner: boolean })[]; me: { owner: boolean; manager: boolean; canShare: boolean } };
export const getTeamInfo = () => call<TeamInfo>("/team");
export const setTeamMember = (email: string, patch: { manager?: boolean; share?: boolean }) =>
  call<{ member: { email: string; manager: boolean; share: boolean } }>("/team/member", { method: "POST", body: { email, ...patch } }).then((d) => d.member);
export const getPeople = (id: string) => call<ShareList>(`/${encodeURIComponent(id)}/people`);
export const setPeople = (id: string, body: ShareList) => call<ShareList>(`/${encodeURIComponent(id)}/people`, { method: "POST", body });

export const listReviews = () => call<{ reviews: ReviewSummary[] }>("").then((d) => d.reviews);
// the list plus whether you're the account owner (missing on servers without sharing)
export const listReviewsInfo = () => call<{ reviews: ReviewSummary[]; owner?: boolean }>("");
export const listDeleted = () => call<{ reviews: ReviewSummary[] }>("/deleted").then((d) => d.reviews);
export const getReview = (id: string) => call<Review>(`/${encodeURIComponent(id)}`);
export const decide = (id: string, body: { itemId: string; decision: "approve" | "change" | "question" | "answer"; reasons?: string[]; note?: string; version?: string; answer?: string; choices?: (number | null)[] }) =>
  call<ReviewSummary>(`/${encodeURIComponent(id)}/decide`, { method: "POST", body });
export const manage = (id: string, body: { action: "rename" | "done" | "reopen" | "delete" | "restore" | "purge"; title?: string }) =>
  call<ReviewSummary & { purged?: boolean }>(`/${encodeURIComponent(id)}/manage`, { method: "POST", body });
// the review's share link: on (the same code each time) or off
export const shareLink = (id: string, off = false) => call<{ token: string | null }>(`/${encodeURIComponent(id)}/share`, { method: "POST", body: off ? { off: true } : {} });
export const myReviewsLink = (off = false) => call<{ token: string | null }>(`/inbox`, { method: "POST", body: off ? { off: true } : {} });
export const shareStatus = (id: string) => call<{ token: string | null }>(`/${encodeURIComponent(id)}/share`);
export const addNote = (id: string, body: { itemId: string; text?: string; audio?: string; audioType?: string; quote?: string; version?: string; anchor?: string; frame?: string; time?: number }) =>
  call<Note>(`/${encodeURIComponent(id)}/notes`, { method: "POST", body });

// a frame comment's picture, as a data address the page can show
export const noteFrame = async (id: string, noteId: string) => { const f = await call<{ contentType: string; data: string }>(`/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}/frame`); return `data:${f.contentType};base64,${f.data}`; };

// change a note's words, or delete it (and its voice recording)
export const editNote = (id: string, noteId: string, body: { text: string } | { delete: true } | { anchor: string; quote: string }) =>
  call<Note & { deleted?: string }>(`/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, { method: "POST", body });

// On a share or My reviews link the files need no sign-in, so audio plays straight from its address:
// iPhone home-screen apps only start audio that begins right from a tap (no download first) and
// handle in-memory copies poorly. Signed in, files need the token, so they come as a blob URL.
export const directFileUrl = (id: string, name: string): string | null => (OPEN ? route(`/${encodeURIComponent(id)}/files/${encodeURIComponent(name)}`) : null);

// audio needs the session token too, so it's fetched and played from a local blob URL
const blobCache = new Map<string, string>();
export async function fileUrl(id: string, name: string): Promise<string> {
  const direct = directFileUrl(id, name);
  if (direct) return direct;
  const key = `${id}/${name}`;
  if (blobCache.has(key)) return blobCache.get(key)!;
  const token = OPEN ? null : getSessionToken();
  const res = await fetch(route(`/${encodeURIComponent(id)}/files/${encodeURIComponent(name)}`), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error("Couldn't load that audio.");
  const url = URL.createObjectURL(await res.blob());
  blobCache.set(key, url);
  return url;
}

export const toBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = () => reject(new Error("Couldn't read the recording."));
    r.readAsDataURL(blob);
  });
